import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Upload, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface ImageUploaderProps {
  onImageUploaded: (imageDataUrl: string | null) => void;
  currentImage?: string | null;
  initialImage?: string | null; // Added to make it compatible with MenuItemImage
  className?: string;
}

export function ImageUploader({
  onImageUploaded,
  currentImage,
  initialImage, // Added to match the prop passed from MenuItemImage
  className,
}: ImageUploaderProps) {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { t } = useTranslation();

  // Initialize with current image or initial image if provided
  useEffect(() => {
    if (currentImage) {
      setSelectedImage(currentImage);
    } else if (initialImage) {
      setSelectedImage(initialImage);
    }
  }, [currentImage, initialImage]);

  const handleImageChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.files && event.target.files[0]) {
      setIsLoading(true);

      try {
        const file = event.target.files[0];

        // Check file size (limit to 5MB)
        if (file.size > 5 * 1024 * 1024) {
          alert('Image is too large. Please select an image under 5MB.');
          setIsLoading(false);
          return;
        }

        const reader = new FileReader();

        reader.onload = (e) => {
          const dataUrl = e.target?.result as string;
          setSelectedImage(dataUrl);
          onImageUploaded(dataUrl);
          setIsLoading(false);
        };

        reader.onerror = () => {
          console.error('Error reading file');
          setIsLoading(false);
        };

        reader.readAsDataURL(file);
      } catch (error) {
        console.error('Error processing image:', error);
        setIsLoading(false);
      }
    }
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    onImageUploaded(null);
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {selectedImage ? (
        <div className='relative'>
          <div className='group relative aspect-square w-full h-40 overflow-hidden rounded-md border bg-muted'>
            <img
              src={selectedImage}
              alt={t('imageUploader.selectedImageAlt')}
              className='h-full w-full object-contain'
            />
            <button
              type='button'
              onClick={handleRemoveImage}
              className='absolute top-2 right-2 rounded-full bg-background/80 p-1 text-muted-foreground hover:text-foreground'>
              <X className='h-4 w-4' />
              <span className='sr-only'>{t('imageUploader.removeImage')}</span>
            </button>
          </div>
        </div>
      ) : (
        <div className='flex flex-col items-center gap-1.5'>
          <div className='w-full'>
            <Button
              type='button'
              variant='outline'
              size='sm'
              className='w-full'
              disabled={isLoading}>
              <label className='flex w-full cursor-pointer items-center justify-center gap-2'>
                <Upload className='h-4 w-4' />
                <span>
                  {isLoading ? 'Uploading...' : t('imageUploader.uploadImage')}
                </span>
                <input
                  type='file'
                  accept='image/*'
                  onChange={handleImageChange}
                  className='sr-only'
                  disabled={isLoading}
                />
              </label>
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
